import { CreditContext } from "@database/contexts"
import { useCallback, useContext, useState } from "react"
import { useOrganisation } from "./useOrganisation";
import { creditServices } from "@database/services";
import { CreditDataType, ErrorType } from "@utilities/types";

export const useCredits = () => {

    const {
        credits, setCredits,
        closedCredits, setClosedCredits,
    } = useContext(CreditContext);
    const { organisation } = useOrganisation();

    const [getAllOrganisationCreditsError, setGetAllOrganisationCreditsError] = useState<ErrorType | null>(null);
    const [getAllOrganisationCreditsLoading, setGetAllOrganisationCreditsLoading] = useState<boolean>(false);
    const [getClosedCreditsError, setGetClosedCreditsError] = useState<ErrorType | null>(null);
    const [getClosedCreditsLoading, setGetClosedCreditsLoading] = useState<boolean>(false);
    const [creditPaymentError, setCreditPaymentError] = useState<ErrorType | null>(null);
    const [creditPaymentLoading, setCreditPaymentLoading] = useState<boolean>(false);
    const [creditPaymentSuccess, setCreditPaymentSuccess] = useState<boolean>(false);

    const onGetAllOrganisationCredits = useCallback(async () => {
        if (!organisation) return;

        setGetAllOrganisationCreditsLoading(true);
        const req = await creditServices.getAllOrganisationCredits(organisation._id);

        if (req.code === 200) {
            setCredits(req.data);
            setGetAllOrganisationCreditsError(null);
        } else {
            setGetAllOrganisationCreditsError({
                message: (req.code === 400 || req.code === 404) ?
                    "Paramettres incorrects" : "Impossible de récupérer les crédits",
            });
        }
        setGetAllOrganisationCreditsLoading(false);
    }, [organisation, setCredits]);

    const onGetClosedCredits = useCallback(async () => {
        if (!organisation) return;

        setGetClosedCreditsLoading(true);
        const req = await creditServices.getClosedCredits(organisation._id);

        if (req.code === 200) {
            setClosedCredits(req.data);
            setGetClosedCreditsError(null);
        } else {
            setGetClosedCreditsError({
                message: (req.code === 400 || req.code === 404) ?
                    "Paramettres incorrects" : "Impossible de récupérer l'historique des crédits",
            });
        }
        setGetClosedCreditsLoading(false);
    }, [organisation, setClosedCredits]);

    const onCreditPayment = useCallback(async (creditID: string, creditData: CreditDataType) => {
        setCreditPaymentLoading(true);
        setCreditPaymentSuccess(false);

        const req = await creditServices.creditPayment(creditID, creditData);
        if (req.code === 200) {
            setCreditPaymentError(null);
            setCreditPaymentSuccess(true);
            await onGetAllOrganisationCredits();
            await onGetClosedCredits();
        } else {
            setCreditPaymentError({
                message: (req.code === 400 || req.code === 404) ?
                    "Paramettres incorrects" : "Impossible d'effectuer le paiement",
            });
        }
        setCreditPaymentLoading(false);
    }, [onGetAllOrganisationCredits, onGetClosedCredits]);

    const resetCreditPaymentStatus = useCallback(() => {
        setCreditPaymentError(null);
        setCreditPaymentSuccess(false);
    }, []);

    return {
        credits, closedCredits,
        getAllOrganisationCreditsError, getAllOrganisationCreditsLoading,
        getClosedCreditsError, getClosedCreditsLoading,
        creditPaymentError, creditPaymentLoading, creditPaymentSuccess,
        onGetAllOrganisationCredits, onGetClosedCredits,
        onCreditPayment, resetCreditPaymentStatus,
    }
}
